import {
  CreateNeedFormDto,
  CreateNeedFormSchema,
  FilterOptionDto,
  NeedDto,
  NeedsCategoryDto,
  NeedsIssueDto,
  UpdateNeedFormDto,
  UpdateNeedFormSchema,
} from '@act/shared/data-transfer-objects';
import { Button, FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Dialog, DialogContent, Divider, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { createNeedSuccess, patchNeedSuccess } from 'redux/slices/needs.slice';
import { needRequests } from 'redux/requests/need.requests';
import {
  metaSliceSelectors,
  selectNeedsCategoryByIdFromMetadata,
  selectNeedsIssueByIdFromMetadata,
  selectNeedsMetadata,
  selectNeedsZcodeByIdFromMetadata,
} from 'redux/slices/metaSlice';
import { selectSelectedMemberId } from 'redux/slices/selectedMemberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import StyledAlert from 'toolkit/alerts/styled-alert';
import { generateDatadogTrackingProps } from 'toolkit/environment/environment/user-monitoring';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import { useToast } from '../app/ReusableToast';
import CloseableDialogTitle from './components/dialog-components/closeable-dialog-title';
import SubmittableDialogActions from './components/dialog-components/submittable-dialog-actions';
import useRichMessageDialog from './hooks/use-rich-message-dialog';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';
import ZCodeContent from './learn-more-zcode-content';
import { TrackingProvider } from 'react-components/tracking';

export interface CreateNeedFormDialogUniqueProps {
  need?: NeedDto;
}

export type CreateNeedFormDialogProps = GenericDialogProps<null> &
  CreateNeedFormDialogUniqueProps;
export type CreateNeedFormDialogCallbackInput = null;

export const CreateNeedFormDialog = ({
  need,
  open,
  setOpen,
}: CreateNeedFormDialogProps) => {
  const openLearnMoreDialog = useRichMessageDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const needsMetadata = useAppSelector(selectNeedsMetadata);
  const selectedMemberId = useAppSelector(selectSelectedMemberId);
  const needIssueOptions = metaSliceSelectors.useSelectNeedIssueOptions();

  const [createdNeedResult, isCreatingNeed, createNeed] = useApiCall(
    needRequests.createNeed
  );
  const [updateNeedResult, isPatchingNeed, patchNeed] = useApiCall(
    needRequests.patchNeed
  );
  const isEditing = !!need;
  const isSubmitting = isCreatingNeed || isPatchingNeed;

  const { control, handleSubmit, watch, formState, setValue, reset } = useForm<
    CreateNeedFormDto | UpdateNeedFormDto
  >({
    resolver: zodResolver(need ? UpdateNeedFormSchema : CreateNeedFormSchema),
    defaultValues: {
      issueId: need?.issueId ?? undefined,
      reason: need?.reason ?? '<p><br></p>',
    },
  });
  const { dirtyFields, isValid, isDirty } = formState;
  const formWatch = watch();
  const watchIssue = watch('issueId');

  const [selectedCategory, setSelectedCategory] =
    useState<NeedsCategoryDto>(null);
  const [selectedIssue, setSelectedIssue] = useState<NeedsIssueDto>(null);

  const selectedIssueZCode = selectNeedsZcodeByIdFromMetadata(
    needsMetadata?.data,
    selectedIssue?.zcodeId
  );

  const formConfig: FormConfig<CreateNeedFormDto | UpdateNeedFormDto> = useMemo(
    () => [
      {
        type: 'input',
        name: 'issueId',
        label: 'Search for need',
        input: 'autocomplete',
        props: {
          options: needIssueOptions,
          getOptionLabel: (option: FilterOptionDto) => option?.label || '',
          required: true,
          groupBy: (option: FilterOptionDto) => option?.tagCloud[0],
          startAdornment: <SearchIcon />,
          trackingName: 'create-need-dialog-issue-autocomplete',
          sensitive: false,
        },
      },
      {
        type: 'passthrough' as const,
        content: selectedCategory ? (
          <Box>
            <StyledAlert
              severity="info"
              title={selectedCategory?.label}
              message={`This issue has a target time window of ≤ ${selectedIssue?.defaultTimeFrameInDays} days.`}
              actions={[
                <Button
                  key="extend-time"
                  onClick={() =>
                    openLearnMoreDialog({
                      title: selectedCategory?.label,
                      content: <ZCodeContent zcode={selectedIssueZCode} />,
                    })
                  }
                  {...generateDatadogTrackingProps(
                    'create-need-dialog-learn-more'
                  )}
                >
                  Learn more
                </Button>,
              ]}
            />
            <Divider sx={{ margin: '16px 0' }} />
          </Box>
        ) : null,
      },
      {
        type: 'label' as const,
        label: 'What is the reason for pursuing this need?*',
      },
      {
        type: 'input',
        name: 'reason',
        label: 'Reason for pursuing this need*',
        input: 'richText',
        props: {
          placeholderText: 'Reason for pursuing this need*',
          required: true,
          defaultValue: need?.reason ?? '<p><br></p>',
        },
      },
    ],
    [
      need,
      needIssueOptions,
      selectedCategory,
      selectedIssue,
      selectedIssueZCode,
      openLearnMoreDialog,
    ]
  );

  useEffect(() => {
    if (watchIssue) {
      const issue = selectNeedsIssueByIdFromMetadata(
        needsMetadata?.data,
        watchIssue
      );
      const category = selectNeedsCategoryByIdFromMetadata(
        needsMetadata?.data,
        issue?.categoryId
      );
      setSelectedIssue(issue);
      setSelectedCategory(category);
    }
  }, [watchIssue]);

  useEffect(() => {
    if (need) {
      setValue('issueId', need.issueId);
      setValue('reason', need.reason);
    } else {
      setValue('issueId', undefined);
      setValue('reason', '<p><br></p>');
    }
  }, [need, setValue]);

  useEffect(() => {
    if (createdNeedResult || updateNeedResult) {
      if (createdNeedResult) {
        dispatch(createNeedSuccess(createdNeedResult));
        toast('Need created successfully');
      }
      if (updateNeedResult) {
        dispatch(patchNeedSuccess(updateNeedResult));
        toast('Need updated successfully');
      }
      setOpen(false);
    }
  }, [createdNeedResult, updateNeedResult]);

  async function handleSubmission() {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    if (isEditing) {
      const body = {
        ...(Object.keys(dirtyFields).includes('issueId') && {
          issueId: formWatch.issueId,
        }),
        ...(Object.keys(dirtyFields).includes('reason') && {
          reason: formWatch.reason,
        }),
      };

      if (Object.keys(body).length) {
        patchNeed({
          body,
          params: { needId: need.id },
        });
      }
    } else {
      createNeed({
        body: {
          memberId: selectedMemberId,
          issueId: formWatch.issueId,
          reason: formWatch.reason,
        },
        params: null,
      });
    }
  }

  const handleClose = () => {
    // Prevent closing during submission
    if (isSubmitting) {
      return;
    }

    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        setOpen(false);
      });
    } else {
      reset();
      setOpen(false);
    }
  };

  return (
    <TrackingProvider contextName="create-need-form-dialog">
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        onSubmit={handleSubmit(handleSubmission)}
      >
        <CloseableDialogTitle
          title={isEditing ? 'Edit need' : 'Add identified need'}
          handleClose={handleClose}
        />
        <DialogContent>
          <Box>
            <Box sx={{ display: 'block' }}>
              <Typography
                variant="titleMedium"
                sx={{ fontWeight: 500, fontSize: '16px' }}
              >
                What is the member's need?
              </Typography>
            </Box>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
              disabled={isSubmitting}
            />
          </Box>
        </DialogContent>
        <SubmittableDialogActions
          cancelText={'Cancel'}
          submissionText={isSubmitting ? 'Saving...' : 'Save and close'}
          isValid={isValid}
          disabled={isSubmitting}
          handleClose={handleClose}
          handleSubmission={handleSubmission}
        />
      </Dialog>
    </TrackingProvider>
  );
};

export default CreateNeedFormDialog;
